import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { staffLeaveAPI } from '../../services/api';
import { PageHeader, Section } from '../Payroll/payrollUi';
import { ROLE_OPTIONS, roleLabel } from '../Payroll/plainLabels';

const WEEKDAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const blankTemplate = () => ({
  name: '',
  role: 'teacher',
  country: 'India',
  lines: [{ leaveTypeName: 'Casual', days: 12 }],
});

const blankCalendar = () => ({
  name: '',
  role: 'teacher',
  country: 'India',
  holidays: [{ date: '', name: '' }],
});

function roleChoices(current) {
  if (current && !ROLE_OPTIONS.some((row) => row.value === current)) {
    return [{ value: current, label: roleLabel(current) }, ...ROLE_OPTIONS];
  }
  return ROLE_OPTIONS;
}

export default function StaffLeaveSetupPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState(0);
  const templates = useQuery(['staff-leave-templates'], async () => (await staffLeaveAPI.listTemplates()).data.data);
  const calendars = useQuery(['staff-leave-calendars'], async () => (await staffLeaveAPI.listCalendars()).data.data);
  const people = useQuery(['staff-leave-people'], async () => (await staffLeaveAPI.listPeople()).data.data);

  const [template, setTemplate] = useState(blankTemplate);
  const [calendar, setCalendar] = useState(blankCalendar);
  const [personId, setPersonId] = useState('');
  const [person, setPerson] = useState({
    dateOfJoining: '',
    exitDate: '',
    reportingManagerUserId: '',
    workingDaysPerWeek: 5,
    weeklyOffDays: [0],
    hoursPerDay: 8,
    leaveTemplateId: '',
    holidayCalendarId: '',
  });

  const saveTemplate = useMutation((body) => staffLeaveAPI.saveTemplate(body), {
    onSuccess: () => {
      qc.invalidateQueries(['staff-leave-templates']);
      toast.success('Leave list saved');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not save the leave list'),
  });
  const saveCalendar = useMutation((body) => staffLeaveAPI.saveCalendar(body), {
    onSuccess: () => {
      qc.invalidateQueries(['staff-leave-calendars']);
      toast.success('Holiday list saved');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not save the holiday list'),
  });
  const savePerson = useMutation(({ userId, body }) => staffLeaveAPI.savePerson(userId, body), {
    onSuccess: () => {
      qc.invalidateQueries(['staff-leave-people']);
      toast.success('Saved for this person');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not save'),
  });

  if (templates.isLoading || calendars.isLoading || people.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (templates.isError || calendars.isError || people.isError) {
    return <Alert severity="error">Could not load leave and holidays.</Alert>;
  }

  const selectedPerson = (people.data || []).find((row) => row.id === personId);
  const salesSupervisors = (people.data || []).filter(
    (row) => row.role === 'sales_supervisor' || row.role === 'country_manager'
  );

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Leave and holidays"
        subtitle="Create a leave list and a holiday list, then give both to a person. They apply from My leave."
      />
      <Tabs value={tab} onChange={(_, value) => setTab(value)}>
        <Tab label="Leave lists" />
        <Tab label="Holiday lists" />
        <Tab label="Give to a person" />
      </Tabs>

      {tab === 0 && (
        <Section
          title={template.id ? template.name || 'Leave list' : 'New leave list'}
          hint="One row is one kind of leave, and how many days a full year allows."
          action={(
            <Button variant="contained" disabled={saveTemplate.isLoading || !template.name.trim()} onClick={() => saveTemplate.mutate({
              id: template.id,
              name: template.name,
              role: template.role,
              country: template.country,
              lines: template.lines
                .filter((line) => String(line.leaveTypeName || '').trim())
                .map((line) => ({ id: line.id, leaveTypeName: line.leaveTypeName, days: Number(line.days) })),
            })}
            >
              Save leave list
            </Button>
          )}
        >
          <Stack spacing={2}>
            <TextField
              select
              label="Which list"
              value={template.id || ''}
              onChange={(e) => {
                const found = (templates.data || []).find((row) => row.id === e.target.value);
                if (!found) {
                  setTemplate(blankTemplate());
                  return;
                }
                setTemplate({
                  id: found.id,
                  name: found.name,
                  role: found.role,
                  country: found.country,
                  lines: (found.lines || []).map((line) => ({ ...line })),
                });
              }}
              fullWidth
            >
              <MenuItem value="">New leave list</MenuItem>
              {(templates.data || []).map((row) => (
                <MenuItem key={row.id} value={row.id}>{row.name} · {roleLabel(row.role)}</MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Name" placeholder="Example: Teachers" value={template.name} onChange={(e) => setTemplate({ ...template, name: e.target.value })} fullWidth />
              <TextField select label="For which job" value={template.role} onChange={(e) => setTemplate({ ...template, role: e.target.value })} fullWidth>
                {roleChoices(template.role).map((row) => <MenuItem key={row.value} value={row.value}>{row.label}</MenuItem>)}
              </TextField>
              <TextField label="Country" value={template.country} onChange={(e) => setTemplate({ ...template, country: e.target.value })} fullWidth />
            </Stack>
            {template.lines.map((line, index) => (
              <Stack key={line.id || index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Leave name"
                  placeholder="Casual"
                  value={line.leaveTypeName}
                  onChange={(e) => {
                    const lines = template.lines.slice();
                    lines[index] = { ...line, leaveTypeName: e.target.value };
                    setTemplate({ ...template, lines });
                  }}
                  fullWidth
                />
                <TextField
                  label="Days in a full year"
                  type="number"
                  value={line.days}
                  onChange={(e) => {
                    const lines = template.lines.slice();
                    lines[index] = { ...line, days: e.target.value };
                    setTemplate({ ...template, lines });
                  }}
                  sx={{ width: { sm: 180 } }}
                />
                <Button onClick={() => setTemplate({ ...template, lines: template.lines.filter((_, i) => i !== index) })} disabled={template.lines.length === 1}>
                  Remove
                </Button>
              </Stack>
            ))}
            <Button onClick={() => setTemplate({ ...template, lines: [...template.lines, { leaveTypeName: '', days: '' }] })}>
              Add a leave type
            </Button>
          </Stack>
        </Section>
      )}

      {tab === 1 && (
        <Section
          title={calendar.id ? calendar.name || 'Holiday list' : 'New holiday list'}
          hint="Add each holiday with its date. Weekly offs are set on the person, not here."
          action={(
            <Button variant="contained" disabled={saveCalendar.isLoading || !calendar.name.trim()} onClick={() => saveCalendar.mutate({
              id: calendar.id,
              name: calendar.name,
              role: calendar.role,
              country: calendar.country,
              holidays: calendar.holidays.filter((row) => row.date && String(row.name || '').trim()),
            })}
            >
              Save holiday list
            </Button>
          )}
        >
          <Stack spacing={2}>
            <TextField
              select
              label="Which list"
              value={calendar.id || ''}
              onChange={(e) => {
                const found = (calendars.data || []).find((row) => row.id === e.target.value);
                if (!found) {
                  setCalendar(blankCalendar());
                  return;
                }
                setCalendar({
                  id: found.id,
                  name: found.name,
                  role: found.role,
                  country: found.country,
                  holidays: (found.holidays || []).map((row) => ({ ...row })),
                });
              }}
              fullWidth
            >
              <MenuItem value="">New holiday list</MenuItem>
              {(calendars.data || []).map((row) => (
                <MenuItem key={row.id} value={row.id}>{row.name} · {roleLabel(row.role)}</MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Name" placeholder="Example: Teachers 2026" value={calendar.name} onChange={(e) => setCalendar({ ...calendar, name: e.target.value })} fullWidth />
              <TextField select label="For which job" value={calendar.role} onChange={(e) => setCalendar({ ...calendar, role: e.target.value })} fullWidth>
                {roleChoices(calendar.role).map((row) => <MenuItem key={row.value} value={row.value}>{row.label}</MenuItem>)}
              </TextField>
              <TextField label="Country" value={calendar.country} onChange={(e) => setCalendar({ ...calendar, country: e.target.value })} fullWidth />
            </Stack>
            {calendar.holidays.map((row, index) => (
              <Stack key={row.id || index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={row.date || ''}
                  onChange={(e) => {
                    const holidays = calendar.holidays.slice();
                    holidays[index] = { ...row, date: e.target.value };
                    setCalendar({ ...calendar, holidays });
                  }}
                  sx={{ width: { sm: 180 } }}
                />
                <TextField
                  label="Holiday name"
                  placeholder="Diwali"
                  value={row.name}
                  onChange={(e) => {
                    const holidays = calendar.holidays.slice();
                    holidays[index] = { ...row, name: e.target.value };
                    setCalendar({ ...calendar, holidays });
                  }}
                  fullWidth
                />
                <Button onClick={() => setCalendar({ ...calendar, holidays: calendar.holidays.filter((_, i) => i !== index) })} disabled={calendar.holidays.length === 1}>
                  Remove
                </Button>
              </Stack>
            ))}
            <Button onClick={() => setCalendar({ ...calendar, holidays: [...calendar.holidays, { date: '', name: '' }] })}>
              Add a holiday
            </Button>
          </Stack>
        </Section>
      )}

      {tab === 2 && (
        <Section
          title="Give lists to a person"
          hint="Also available on Staff profiles, under Work. Joining date, days off, leave list, and holiday list are all required."
          action={(
            <Button
              variant="contained"
              disabled={!personId || savePerson.isLoading}
              onClick={() => savePerson.mutate({
                userId: personId,
                body: {
                  ...person,
                  exitDate: person.exitDate || null,
                  reportingManagerUserId: person.reportingManagerUserId || null,
                  workingDaysPerWeek: Number(person.workingDaysPerWeek),
                  hoursPerDay: Number(person.hoursPerDay),
                },
              })}
            >
              Save
            </Button>
          )}
        >
          <Stack spacing={2}>
            <TextField
              select
              label="Person"
              value={personId}
              onChange={(e) => {
                const id = e.target.value;
                setPersonId(id);
                const found = (people.data || []).find((row) => row.id === id);
                if (!found) return;
                setPerson({
                  dateOfJoining: found.dateOfJoining || '',
                  exitDate: found.exitDate || '',
                  reportingManagerUserId: found.reportingManagerUserId || '',
                  workingDaysPerWeek: found.workingDaysPerWeek ?? 5,
                  weeklyOffDays: found.weeklyOffDays || [0],
                  hoursPerDay: found.hoursPerDay ?? 8,
                  leaveTemplateId: found.leaveTemplateId || '',
                  holidayCalendarId: found.holidayCalendarId || '',
                });
              }}
              fullWidth
            >
              {(people.data || []).map((row) => (
                <MenuItem key={row.id} value={row.id}>{row.name} · {roleLabel(row.role)}</MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Joining date" type="date" InputLabelProps={{ shrink: true }} value={person.dateOfJoining} onChange={(e) => setPerson({ ...person, dateOfJoining: e.target.value })} fullWidth />
              <TextField label="Last working day" type="date" InputLabelProps={{ shrink: true }} value={person.exitDate} onChange={(e) => setPerson({ ...person, exitDate: e.target.value })} helperText="Leave blank until they leave" fullWidth />
              <TextField label="Working days each week" type="number" value={person.workingDaysPerWeek} onChange={(e) => setPerson({ ...person, workingDaysPerWeek: e.target.value })} fullWidth />
              <TextField label="Hours each day" type="number" value={person.hoursPerDay} onChange={(e) => setPerson({ ...person, hoursPerDay: e.target.value })} fullWidth />
            </Stack>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Days off each week</Typography>
              <ToggleButtonGroup
                value={person.weeklyOffDays}
                onChange={(_, value) => setPerson({ ...person, weeklyOffDays: value || [] })}
                size="small"
              >
                {WEEKDAYS.map((day) => <ToggleButton key={day.value} value={day.value}>{day.label}</ToggleButton>)}
              </ToggleButtonGroup>
            </Box>
            <TextField select label="Leave list" value={person.leaveTemplateId} onChange={(e) => setPerson({ ...person, leaveTemplateId: e.target.value })} fullWidth>
              {(templates.data || []).filter((row) => row.isActive !== false).map((row) => (
                <MenuItem key={row.id} value={row.id}>{row.name} · {roleLabel(row.role)}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Holiday list" value={person.holidayCalendarId} onChange={(e) => setPerson({ ...person, holidayCalendarId: e.target.value })} fullWidth>
              {(calendars.data || []).filter((row) => row.isActive !== false).map((row) => (
                <MenuItem key={row.id} value={row.id}>{row.name} · {roleLabel(row.role)}</MenuItem>
              ))}
            </TextField>
            {selectedPerson?.role === 'sales' && (
              <TextField select label="Sales supervisor" value={person.reportingManagerUserId || ''} onChange={(e) => setPerson({ ...person, reportingManagerUserId: e.target.value })} fullWidth>
                <MenuItem value="">Choose one</MenuItem>
                {salesSupervisors.map((row) => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>)}
              </TextField>
            )}
          </Stack>
        </Section>
      )}
    </Stack>
  );
}
